import { createServerFn } from "@tanstack/react-start";
import { createClient } from "./supabase/admin";
import type { Database } from "./supabase/schema";
import { supabaseUserMiddleware } from "@/middleware/supabase-user-middleware";
import {
  OrderTypes,
  ProductTypes,
  ProductVisibilityTypes,
  Status,
  Target,
  UpsellContexts,
  V2OptionGroupContexts,
  V2OptionType,
} from "./supabase/enums";
import { queryOptions } from "@tanstack/react-query";
import Decimal from "decimal.js";
import { endOfDay, format, getHours, getMinutes, parseISO, set, startOfDay } from "date-fns";
import { fromZonedTime, toZonedTime } from "date-fns-tz";
import { orderTagOptionGroupLinks, sortV2Options } from "@vrodex/utils";
import { loadMenuCourseTagItemRows } from "./menu-course-tags";
import { normalizePickCount } from "./menu-picks";
import { SalesChannels } from "./storefront/channel";
import { loadStorefrontConfiguration } from "./storefront/configuration";
import { loadAvailableStock } from "./storefront/stock";

const timezone = "Europe/Amsterdam";

export type PostType = {
  id: string;
  title: string;
  body: string;
};

export const productsQueryOptions = () =>
  queryOptions({
    queryKey: ["products"],
    queryFn: fetchProducts,
  });

/**
 * Converts a time string from the database to today's date with that time,
 * accounting for DST in the specified timezone.
 */
const getTimeOnToday = (timeString: string): Date => {
  // Parse the ISO string to get the original datetime
  const originalTime = parseISO(timeString);

  // Convert to timezone to extract hours and minutes
  const timeInTimezone = toZonedTime(originalTime, timezone);
  const hours = getHours(timeInTimezone);
  const minutes = getMinutes(timeInTimezone);

  // Get today's date in the timezone
  const now = new Date();
  const todayInTimezone = toZonedTime(now, timezone);
  const todayStart = startOfDay(todayInTimezone);

  // Set the time on today's date
  const todayWithTime = set(todayStart, {
    hours,
    minutes,
    seconds: 0,
    milliseconds: 0,
  });

  // Convert back to UTC
  return fromZonedTime(todayWithTime, timezone);
};

/**
 * Combines product and category date restrictions into a single availability
 * window. The earliest orderable moment is the latest "from" of the two; the
 * latest orderable moment is the earliest "until" of the two. "until" is
 * treated as end-of-day so the until date itself remains orderable.
 */
const resolveAvailabilityWindow = (
  productFrom: string | null | undefined,
  productUntil: string | null | undefined,
  categoryFrom: string | null | undefined,
  categoryUntil: string | null | undefined,
): {
  fromDate?: Date;
  untilDate?: Date;
  isWithinDateRange: boolean;
} => {
  const froms = [productFrom, categoryFrom]
    .filter((v): v is string => !!v)
    .map((v) => parseISO(v));
  const untils = [productUntil, categoryUntil]
    .filter((v): v is string => !!v)
    .map((v) => endOfDay(parseISO(v)));

  const fromDate =
    froms.length > 0
      ? new Date(Math.max(...froms.map((d) => d.getTime())))
      : undefined;
  const untilDate =
    untils.length > 0
      ? new Date(Math.min(...untils.map((d) => d.getTime())))
      : undefined;

  const now = new Date();
  const afterFrom = !fromDate || now >= fromDate;
  const beforeUntil = !untilDate || now <= untilDate;

  return {
    fromDate,
    untilDate,
    isWithinDateRange: afterFrom && beforeUntil,
  };
};

/**
 * Combines product and category time-of-day restrictions into a single window
 * anchored to today. The earliest orderable moment is the latest "from" of the
 * two; the latest orderable moment is the earliest "until" of the two.
 */
const resolveTimeWindow = (
  productFromTime: string | null | undefined,
  productUntilTime: string | null | undefined,
  categoryFromTime: string | null | undefined,
  categoryUntilTime: string | null | undefined,
): {
  fromTime?: Date;
  untilTime?: Date;
} => {
  const froms = [productFromTime, categoryFromTime]
    .filter((v): v is string => !!v)
    .map((v) => getTimeOnToday(v));
  const untils = [productUntilTime, categoryUntilTime]
    .filter((v): v is string => !!v)
    .map((v) => getTimeOnToday(v));

  const fromTime =
    froms.length > 0
      ? new Date(Math.max(...froms.map((d) => d.getTime())))
      : undefined;
  const untilTime =
    untils.length > 0
      ? new Date(Math.min(...untils.map((d) => d.getTime())))
      : undefined;

  return { fromTime, untilTime };
};

export const fetchProducts = createServerFn({ method: "GET" })
  .middleware([supabaseUserMiddleware])
  .handler(async ({ context: { userId, channel, onlineOrderConfigurationId } }) => {
    const supabase = createClient();
    const isStorefront = channel === SalesChannels.storefront;

    // Fetch products
    const productsPromise = supabase
      .from("product")
      .select(
        "id, type, display_name, age_restricted, available_from_time, available_until_time, available_from_date, available_until_date, allergens:allergen_to_product(allergen), name, productCategoryId:product_category_id, position, onlineOrderPosition:online_order_position, slug, visibility:visibility_types_new, description, featuredImage:featured_image_id(url), images:image_for_product(position, image(url)), featured, allergies, vatRateId:vat_rate_id, status, createdDate:created_date, stockManaged:stock_managed, currentStock:current_stock, stockSourceProductId:stock_source_product_id, unitsPerSourceProduct:units_per_source_product, prices:price_on_product(priceId:price_id, value:new_value, oldValue:value), variants:product_variant(id, name, position, status, prices:price_on_product_variant(priceId:price_id, value))"
      )
      // An empty/null visibility list now means "hidden everywhere", so only
      // products explicitly marked for pickup, delivery or QR ordering are
      // fetched online. One query serves the shop's own site, QR ordering and
      // the storefront; selectShopMenu / selectQrMenu / selectStorefrontCatalog
      // split it per channel.
      .or(
        `visibility_types_new.ov.{${ProductVisibilityTypes.pickup},${ProductVisibilityTypes.delivery},${ProductVisibilityTypes.qr},${ProductVisibilityTypes.webshop}}`
      )
      // Regular products AND menu products (a menu is a product whose type is
      // "menu" with a fixed base price and per-course item selection).
      .in("type", [ProductTypes.product, ProductTypes.menu])
      // Menu-scoped products exist only inside their menu product on the POS,
      // so they are never listed on their own; a menu product itself has a null
      // menu_product_id and is included above.
      .is("menu_product_id", null)
      // Modifier products are only offered inside an option group; their
      // option data is fetched by id further down, so leaving them out of the
      // listing does not affect option names or prices.
      .eq("is_modifier", false)
      .eq("user_id", userId)
      .neq("status", Status.trashed)
      .order("position", { ascending: true });

    // Fetch categories
    const productCategoriesPromise = supabase
      .from("product_category")
      .select("id, name, display_name, position, onlineOrderPosition:online_order_position, description, available_from_date, available_until_date, available_from_time, available_until_time, target")
      .or(
        `target.is.null,target.eq.{},target.ov.{${Target.pickup},${Target.delivery},${Target.qr},${Target.webshop}}`
      )
      .eq("status", Status.active)
      .eq("user_id", userId)
      .order("position", { ascending: true });

    // Fetch online order configuration. The default one, which is the one a
    // domain resolves to; a merchant without a domain, served for QR ordering
    // on the stable host, usually has none marked default. A storefront sells
    // from one price list, so both tiers point at it and every price below
    // resolves the same for shipped and picked-up orders.
    const onlineOrderConfigurationPromise = isStorefront
      ? loadStorefrontConfiguration(
          supabase,
          userId,
          onlineOrderConfigurationId
        ).then((result) =>
          result.data
            ? {
                data: {
                  delivery_price_id: result.data.priceId,
                  take_away_price_id: result.data.priceId,
                },
                error: null,
              }
            : { data: null, error: result.error }
        )
      : supabase
          .from("online_order_configuration")
          .select("delivery_price_id, take_away_price_id")
          .eq("user_id", userId)
          .order("is_default", { ascending: false })
          .order("id")
          .limit(1)
          .single();

    // Fetch v2 option groups for categories
    const categoryOptionGroupsPromise = supabase
      .from("v2_category_option_group")
      .select(
        "id, category_id, option_group_id, position, option_group:v2_option_group(id, name, min_selections, max_selections, position, status, option_sort_order)"
      )
      .eq("option_group.status", Status.active)
      .order("position", { ascending: true });

    // Fetch v2 option groups linked to product tags. Every product carrying
    // the tag offers the group, like a category link does.
    const tagOptionGroupsPromise = supabase
      .from("v2_tag_option_group")
      .select(
        "id, tag_id, option_group_id, position, tag:product_tag(position), option_group:v2_option_group(id, name, min_selections, max_selections, position, status, option_sort_order)"
      )
      .eq("user_id", userId)
      .eq("option_group.status", Status.active)
      .order("position", { ascending: true });

    // Which tags each product carries
    const productTagLinksPromise = supabase
      .from("product_tag_to_product")
      .select("product_id, product_tag_id")
      .eq("user_id", userId);

    // Fetch v2 option groups for products
    const productOptionGroupsPromise = supabase
      .from("v2_product_option_group")
      .select(
        "id, product_id, option_group_id, is_enabled, position, min_selections, max_selections, show_on_add_to_cart, option_group:v2_option_group(id, name, min_selections, max_selections, position, status, show_on_add_to_cart, option_sort_order)"
      )
      .eq("is_enabled", true)
      .eq("option_group.status", Status.active)
      .order("position", { ascending: true });

    // Fetch ALL product group override rows (including disabled) so we can
    // apply product-level field/context overrides to category-assigned groups
    // too. Separate from productOptionGroupsPromise, which filters by
    // is_enabled and drives the product-assigned-group path.
    const productGroupOverrideRowsPromise = supabase
      .from("v2_product_option_group")
      .select(
        "id, product_id, option_group_id, is_enabled, min_selections, max_selections, show_on_add_to_cart"
      );

    // Fetch all product-level group context overrides.
    const productGroupContextRowsPromise = supabase
      .from("v2_product_option_group_context")
      .select("product_option_group_id, context");

    // Fetch category option overrides
    const categoryOptionOverridesPromise = supabase
      .from("v2_category_option_override")
      .select("category_id, option_id, is_enabled, price_override_in_cents");

    // Fetch product option overrides
    const productOptionOverridesPromise = supabase
      .from("v2_product_option_override")
      .select("id, product_id, option_id, is_enabled, price_override_in_cents");

    // Fetch all product-level option override context rows.
    // Convention: an override with NO context rows applies in all contexts
    // (use is_enabled normally); an override with rows must include "online"
    // for the option to be shown in online ordering for that product.
    const productOptionOverrideContextRowsPromise = supabase
      .from("v2_product_option_override_context" as any)
      .select("product_option_override_id, context");

    // Fetch option group IDs that have online context (needed to filter options)
    const onlineOptionGroupContextsPromise = supabase
      .from("v2_option_group_context")
      .select("option_group_id")
      .eq("context", V2OptionGroupContexts.online);

    // Fetch option-level context rows.
    // Convention: an option with NO context rows inherits its group's contexts;
    // an option with rows must include "online" to be shown in online ordering.
    const optionContextsPromise = supabase
      .from("v2_product_option_context" as any)
      .select("option_id, context");

    // Get today's date in YYYY-MM-DD format (in the timezone)
    const now = new Date();
    const todayInTimezone = toZonedTime(now, timezone);
    const todayDateString = format(todayInTimezone, "yyyy-MM-dd");

    // Fetch product day availability overrides active as of today (an override can span
    // multiple days or be indefinite, so fetch all anchored on/before today, latest first).
    const productDayAvailabilityPromise = supabase
      .from("product_day_availability")
      .select("product_id, is_available, date, unavailable_until")
      .lte("date", todayDateString)
      .order("date", { ascending: false });

    // Fetch product category day availability overrides active as of today
    const categoryDayAvailabilityPromise = supabase
      .from("product_category_day_availability")
      .select("product_category_id, is_available, date, unavailable_until")
      .lte("date", todayDateString)
      .order("date", { ascending: false });

    // --- Menu products (product.type = "menu") structure ---
    // Which products each menu offers per course, plus the per-course settings
    // (required/optional, display name) and the courses themselves. These are
    // fetched for the whole user and grouped in memory by menu_product_id.
    const menuCourseItemsPromise = supabase
      .from("menu_course_item" as any)
      .select(
        "menu_product_id, course_id, product_id, position, price_supplement_in_cents"
      )
      .eq("user_id", userId);

    const menuCourseSettingsPromise = supabase
      .from("menu_course_setting" as any)
      .select(
        "menu_product_id, course_id, is_required, position, name_override, pick_count"
      )
      .eq("user_id", userId);

    const menuCoursesPromise = supabase
      .from("course" as any)
      .select("id, name, position, status, menu_product_id")
      .eq("user_id", userId);

    // Fetch product↔ingredient links where the linked ingredient is currently
    // unavailable (unavailable_until in the future). Any product with at least
    // one such row is treated as unavailable for online ordering.
    const unavailableIngredientLinksPromise = supabase
      .from("ingredient_to_product" as any)
      .select(
        "product_id, ingredient:ingredient!inner(unavailable_until)"
      )
      .eq("user_id", userId)
      .not("ingredient.unavailable_until", "is", null)
      .gt("ingredient.unavailable_until", now.toISOString());


    // --- Upsells ---
    // The "would you like fries with that" offer shown after a product is
    // configured. Only groups carrying the "online" context are fetched; the
    // kiosk-only ones never reach this app.
    const onlineUpsellGroupsPromise = supabase
      .from("upsell_group")
      .select(
        "id, name, title, description, max_selections, position, upsell_group_context!inner(context)"
      )
      .eq("user_id", userId)
      .eq("status", Status.active)
      .eq("upsell_group_context.context", UpsellContexts.online);

    const upsellItemsPromise = supabase
      .from("upsell_item")
      .select(
        "id, upsell_group_id, product_id, name, description, price_override_in_cents, position"
      )
      .eq("user_id", userId)
      .eq("status", Status.active)
      .order("position", { ascending: true });

    const categoryUpsellGroupsPromise = supabase
      .from("category_upsell_group")
      .select("category_id, upsell_group_id, position")
      .eq("user_id", userId);

    const productUpsellGroupsPromise = supabase
      .from("product_upsell_group")
      .select("product_id, upsell_group_id, is_enabled, position")
      .eq("user_id", userId);

    const [
      productsResult,
      productCategoriesResult,
      onlineOrderConfigurationResult,
      categoryOptionGroupsResult,
      tagOptionGroupsResult,
      productTagLinksResult,
      productOptionGroupsResult,
      productGroupOverrideRowsResult,
      productGroupContextRowsResult,
      onlineOptionGroupContextsResult,
      optionContextsResult,
      categoryOptionOverridesResult,
      productOptionOverridesResult,
      productOptionOverrideContextRowsResult,
      productDayAvailabilityResult,
      categoryDayAvailabilityResult,
      unavailableIngredientLinksResult,
      menuCourseItemsResult,
      menuCourseSettingsResult,
      menuCoursesResult,
      onlineUpsellGroupsResult,
      upsellItemsResult,
      categoryUpsellGroupsResult,
      productUpsellGroupsResult,
    ] = await Promise.all([
      productsPromise,
      productCategoriesPromise,
      onlineOrderConfigurationPromise,
      categoryOptionGroupsPromise,
      tagOptionGroupsPromise,
      productTagLinksPromise,
      productOptionGroupsPromise,
      productGroupOverrideRowsPromise,
      productGroupContextRowsPromise,
      onlineOptionGroupContextsPromise,
      optionContextsPromise,
      categoryOptionOverridesPromise,
      productOptionOverridesPromise,
      productOptionOverrideContextRowsPromise,
      productDayAvailabilityPromise,
      categoryDayAvailabilityPromise,
      unavailableIngredientLinksPromise,
      menuCourseItemsPromise,
      menuCourseSettingsPromise,
      menuCoursesPromise,
      onlineUpsellGroupsPromise,
      upsellItemsPromise,
      categoryUpsellGroupsPromise,
      productUpsellGroupsPromise,
    ]);

    if (productGroupOverrideRowsResult.error) {
      console.error(productGroupOverrideRowsResult.error);
      throw new Error("Could not get product option group override rows.");
    }

    if (productGroupContextRowsResult.error) {
      console.error(productGroupContextRowsResult.error);
      throw new Error("Could not get product option group context overrides.");
    }

    // Group context override rows by product_option_group_id.
    const productGroupContextsByRowId = new Map<string, Set<string>>();
    (productGroupContextRowsResult.data ?? []).forEach((row) => {
      const existing = productGroupContextsByRowId.get(
        row.product_option_group_id
      );
      if (existing) {
        existing.add(row.context);
      } else {
        productGroupContextsByRowId.set(
          row.product_option_group_id,
          new Set([row.context])
        );
      }
    });

    // Index product group override rows by (product_id, option_group_id).
    interface ProductGroupOverrideResolved {
      rowId: string;
      isEnabled: boolean;
      minSelections: number | null;
      maxSelections: number | null;
      showOnAddToCart: boolean | null;
      // null = inherit group-level contexts; non-null = explicit override set
      contexts: Set<string> | null;
    }
    const productGroupOverridesByProductAndGroup = new Map<
      string,
      Map<string, ProductGroupOverrideResolved>
    >();
    (productGroupOverrideRowsResult.data ?? []).forEach((row) => {
      const contexts = productGroupContextsByRowId.get(row.id) ?? null;
      if (!productGroupOverridesByProductAndGroup.has(row.product_id)) {
        productGroupOverridesByProductAndGroup.set(row.product_id, new Map());
      }
      productGroupOverridesByProductAndGroup
        .get(row.product_id)!
        .set(row.option_group_id, {
          rowId: row.id,
          isEnabled: row.is_enabled ?? true,
          minSelections: row.min_selections ?? null,
          maxSelections: row.max_selections ?? null,
          showOnAddToCart: row.show_on_add_to_cart ?? null,
          contexts: contexts && contexts.size > 0 ? contexts : null,
        });
    });

    if (onlineOptionGroupContextsResult.error) {
      console.error(onlineOptionGroupContextsResult.error);
      throw new Error("Could not get online option group contexts.");
    }

    if (optionContextsResult.error) {
      console.error(optionContextsResult.error);
      throw new Error("Could not get option contexts.");
    }

    const onlineOptionGroupIdsBase = new Set(
      onlineOptionGroupContextsResult.data.map((row) => row.option_group_id)
    );

    // Expand with groups that have a product-level override adding "online"
    // for some product (so we still fetch their options).
    productGroupOverridesByProductAndGroup.forEach((groupMap) => {
      groupMap.forEach((override, optionGroupId) => {
        if (
          override.contexts &&
          override.contexts.has(V2OptionGroupContexts.online)
        ) {
          onlineOptionGroupIdsBase.add(optionGroupId);
        }
      });
    });

    const onlineOptionGroupIds = Array.from(onlineOptionGroupIdsBase);

    // Group option-level contexts by option_id so we can apply the
    // "empty = inherit group contexts; non-empty = explicit list" rule.
    const optionContextsByOptionId = new Map<string, Set<string>>();
    ((optionContextsResult.data ?? []) as unknown as Array<{
      option_id: string;
      context: string;
    }>).forEach((row) => {
      const existing = optionContextsByOptionId.get(row.option_id);
      if (existing) {
        existing.add(row.context);
      } else {
        optionContextsByOptionId.set(row.option_id, new Set([row.context]));
      }
    });

    // Fetch all v2 options from option groups that have the online context.
    // Option-level context filtering is applied in-memory below.
    const optionsResult = await supabase
      .from("v2_option")
      .select("id, name, option_group_id, price_in_cents, use_product_price, position, type, status, product_id")
      .eq("status", Status.active)
      .in("type", [V2OptionType.single, V2OptionType.product])
      .in("option_group_id", onlineOptionGroupIds.length > 0 ? onlineOptionGroupIds : ["__no_match__"])
      .order("position", { ascending: true });


    if (productsResult.error) {
      console.error(productsResult.error);
      throw new Error("Could not get products.");
    }

    if (productCategoriesResult.error) {
      console.error(productCategoriesResult.error);
      throw new Error("Could not get product categories.");
    }

    if (onlineOrderConfigurationResult.error) {
      console.error(onlineOrderConfigurationResult.error);
      throw new Error("Could not get online order configuration.");
    }

    if (categoryOptionGroupsResult.error) {
      console.error(categoryOptionGroupsResult.error);
      throw new Error("Could not get category option groups.");
    }

    if (tagOptionGroupsResult.error) {
      console.error(tagOptionGroupsResult.error);
      throw new Error("Could not get tag option groups.");
    }

    if (productTagLinksResult.error) {
      console.error(productTagLinksResult.error);
      throw new Error("Could not get product tags.");
    }

    if (productOptionGroupsResult.error) {
      console.error(productOptionGroupsResult.error);
      throw new Error("Could not get product option groups.");
    }

    if (optionsResult.error) {
      console.error(optionsResult.error);
      throw new Error("Could not get options.");
    }

    // Apply option-level context filter:
    // - no context rows for an option → inherit from its group (include, since
    //   the group is already online-scoped)
    // - explicit rows → only include if "online" is among them
    const contextFilteredOptions = optionsResult.data.filter((option) => {
      const contexts = optionContextsByOptionId.get(option.id);
      if (!contexts || contexts.size === 0) return true;
      return contexts.has(V2OptionGroupContexts.online);
    });

    if (categoryOptionOverridesResult.error) {
      console.error(categoryOptionOverridesResult.error);
      throw new Error("Could not get category option overrides.");
    }

    if (productOptionOverridesResult.error) {
      console.error(productOptionOverridesResult.error);
      throw new Error("Could not get product option overrides.");
    }

    if (productOptionOverrideContextRowsResult.error) {
      console.error(productOptionOverrideContextRowsResult.error);
      throw new Error("Could not get product option override contexts.");
    }

    // Build a map of override_id → set of contexts so each override carries
    // its full context list. Empty/missing means "applies in all contexts";
    // an explicit list must include "online" to apply in online ordering.
    const productOverrideContextsByOverrideId = new Map<string, Set<string>>();
    (
      (productOptionOverrideContextRowsResult.data ?? []) as unknown as Array<{
        product_option_override_id: string;
        context: string;
      }>
    ).forEach((row) => {
      const existing = productOverrideContextsByOverrideId.get(
        row.product_option_override_id
      );
      if (existing) {
        existing.add(row.context);
      } else {
        productOverrideContextsByOverrideId.set(
          row.product_option_override_id,
          new Set([row.context])
        );
      }
    });

    if (productDayAvailabilityResult.error) {
      console.error(productDayAvailabilityResult.error);
      throw new Error("Could not get product day availability.");
    }

    if (categoryDayAvailabilityResult.error) {
      console.error(categoryDayAvailabilityResult.error);
      throw new Error("Could not get category day availability.");
    }

    // Collapse day-availability rows (ordered date DESC) into the effective availability
    // of the override active right now, per entity. An "unavailable" override stays active
    // until `unavailable_until` (null = indefinite); an "available" (force-show) override
    // only applies to today. Entities with no active override are absent from the map.
    const buildActiveOverrideMap = <
      T extends {
        is_available: boolean;
        date: string;
        unavailable_until: string | null;
      },
    >(
      rows: T[],
      getId: (row: T) => string
    ): Map<string, boolean> => {
      const map = new Map<string, boolean>();
      for (const row of rows) {
        const id = getId(row);
        if (map.has(id)) continue;
        if (row.is_available === false) {
          if (
            row.unavailable_until === null ||
            new Date(row.unavailable_until) > now
          ) {
            map.set(id, false);
          }
          // expired unavailable override — keep scanning older rows for this entity
          continue;
        }
        if (row.date === todayDateString) {
          map.set(id, true);
        }
      }
      return map;
    };

    // Create a map of category availability for today
    const categoryAvailabilityMap = buildActiveOverrideMap(
      categoryDayAvailabilityResult.data,
      (row) => row.product_category_id
    );

    // Create a map of product availability for today
    const productAvailabilityMap = buildActiveOverrideMap(
      productDayAvailabilityResult.data,
      (row) => row.product_id
    );

    // Set of product IDs blocked by at least one unavailable ingredient
    const productsBlockedByIngredients = new Set<string>();
    if (!unavailableIngredientLinksResult.error) {
      ((unavailableIngredientLinksResult.data ?? []) as unknown as {
        product_id: string;
      }[]).forEach((link) => {
        productsBlockedByIngredients.add(link.product_id);
      });
    }

    // Extract product IDs from product options
    const productOptionProductIds = contextFilteredOptions
      .filter((option) => option.type === V2OptionType.product && option.product_id)
      .map((option) => option.product_id!)
      .filter((id, index, self) => self.indexOf(id) === index); // Remove duplicates

    // Fetch product data for product options
    let productOptionsData = new Map<string, {
      id: string;
      name: string;
      display_name: string | null;
      takeAwayPrice: number | undefined;
      deliveryPrice: number | undefined;
      isAvailableToday: boolean;
    }>();

    if (productOptionProductIds.length > 0) {
      const productOptionsResult = await supabase
        .from("product")
        .select(
          "id, name, display_name, status, prices:price_on_product(priceId:price_id, value:new_value, oldValue:value)"
        )
        .in("id", productOptionProductIds)
        .eq("type", ProductTypes.product)
        .eq("user_id", userId)
        .neq("status", Status.trashed);

      if (productOptionsResult.error) {
        console.error(productOptionsResult.error);
        throw new Error("Could not get product options data.");
      }

      productOptionsResult.data.forEach((product) => {
        const takeAwayPrice = product.prices.find(
          (price) =>
            price.priceId === onlineOrderConfigurationResult.data.take_away_price_id
        );
        const deliveryPrice = product.prices.find(
          (price) =>
            price.priceId === onlineOrderConfigurationResult.data.delivery_price_id
        );

        const blockedByIngredient = productsBlockedByIngredients.has(product.id);
        const isAvailableToday =
          blockedByIngredient
            ? false
            : product.status === Status.active
              ? productAvailabilityMap.has(product.id)
                ? productAvailabilityMap.get(product.id) ?? true
                : true
              : product.status === Status.inactive
                ? productAvailabilityMap.has(product.id) &&
                  productAvailabilityMap.get(product.id) === true
                : false;

        productOptionsData.set(product.id, {
          id: product.id,
          name: product.name,
          display_name: product.display_name,
          takeAwayPrice: takeAwayPrice?.value
            ? new Decimal(takeAwayPrice.value).div(100).toNumber()
            : takeAwayPrice?.oldValue,
          deliveryPrice: deliveryPrice?.value
            ? new Decimal(deliveryPrice.value).div(100).toNumber()
            : deliveryPrice?.oldValue,
          isAvailableToday,
        });
      });
    }

    // --- Upsell offers ---
    // A missing table (migration not pushed yet) must not take the whole menu
    // down, so a failed upsell query degrades to "this shop has no upsells".
    const onlineUpsellGroups = onlineUpsellGroupsResult.error
      ? []
      : (onlineUpsellGroupsResult.data ?? []);
    const upsellItemRows = upsellItemsResult.error
      ? []
      : (upsellItemsResult.data ?? []);
    const categoryUpsellRows = categoryUpsellGroupsResult.error
      ? []
      : (categoryUpsellGroupsResult.data ?? []);
    const productUpsellRows = productUpsellGroupsResult.error
      ? []
      : (productUpsellGroupsResult.data ?? []);

    if (onlineUpsellGroupsResult.error) {
      console.error(onlineUpsellGroupsResult.error);
    }

    const onlineUpsellGroupIds = new Set(
      onlineUpsellGroups.map((group) => group.id)
    );

    const upsellItemsByGroupId = new Map<string, typeof upsellItemRows>();
    upsellItemRows.forEach((row) => {
      if (!onlineUpsellGroupIds.has(row.upsell_group_id)) return;
      const existing = upsellItemsByGroupId.get(row.upsell_group_id);
      if (existing) existing.push(row);
      else upsellItemsByGroupId.set(row.upsell_group_id, [row]);
    });

    // The offered products are fetched separately: an upsell can point at a
    // product that is not itself listed online (a side that only exists as an
    // add-on), and it still needs a name, an image and a price.
    const upsellProductIds = Array.from(
      new Set(
        Array.from(upsellItemsByGroupId.values())
          .flat()
          .map((row) => row.product_id)
      )
    );

    interface UpsellProductData {
      id: string;
      name: string;
      categoryId: string | null;
      featuredImage?: string;
      takeAwayPrice: number | undefined;
      deliveryPrice: number | undefined;
      isAvailableToday: boolean;
    }
    const upsellProductsById = new Map<string, UpsellProductData>();

    if (upsellProductIds.length > 0) {
      const upsellProductsResult = await supabase
        .from("product")
        .select(
          "id, name, display_name, status, categoryId:product_category_id, featuredImage:featured_image_id(url), prices:price_on_product(priceId:price_id, value:new_value, oldValue:value)"
        )
        .in("id", upsellProductIds)
        .eq("type", ProductTypes.product)
        // A modifier product would become a standalone line, which checkout
        // rejects; it is only offered inside an option group.
        .eq("is_modifier", false)
        .eq("user_id", userId)
        .neq("status", Status.trashed);

      if (upsellProductsResult.error) {
        console.error(upsellProductsResult.error);
      } else {
        upsellProductsResult.data.forEach((product) => {
          const takeAwayPrice = product.prices.find(
            (price) =>
              price.priceId ===
              onlineOrderConfigurationResult.data.take_away_price_id
          );
          const deliveryPrice = product.prices.find(
            (price) =>
              price.priceId ===
              onlineOrderConfigurationResult.data.delivery_price_id
          );

          const blockedByIngredient = productsBlockedByIngredients.has(
            product.id
          );
          const isAvailableToday = blockedByIngredient
            ? false
            : product.status === Status.active
              ? productAvailabilityMap.get(product.id) ?? true
              : product.status === Status.inactive
                ? productAvailabilityMap.get(product.id) === true
                : false;

          upsellProductsById.set(product.id, {
            id: product.id,
            name: product.display_name ?? product.name,
            categoryId: product.categoryId ?? null,
            featuredImage: product.featuredImage?.url ?? undefined,
            takeAwayPrice: takeAwayPrice?.value
              ? new Decimal(takeAwayPrice.value).div(100).toNumber()
              : takeAwayPrice?.oldValue,
            deliveryPrice: deliveryPrice?.value
              ? new Decimal(deliveryPrice.value).div(100).toNumber()
              : deliveryPrice?.oldValue,
            isAvailableToday,
          });
        });
      }
    }

    /**
     * The upsell groups a product offers, in display order.
     *
     * Resolution mirrors the option groups: a group arrives through the
     * product's category or through a direct product assignment, and a product
     * row with is_enabled false removes a group the category handed down.
     */
    const buildProductUpsells = (
      productId: string,
      categoryId: string | null
    ) => {
      const positionByGroupId = new Map<string, number>();

      if (categoryId) {
        categoryUpsellRows
          .filter((row) => row.category_id === categoryId)
          .forEach((row) => {
            positionByGroupId.set(row.upsell_group_id, row.position ?? 0);
          });
      }

      productUpsellRows
        .filter((row) => row.product_id === productId)
        .forEach((row) => {
          if (row.is_enabled) {
            positionByGroupId.set(row.upsell_group_id, row.position ?? 0);
          } else {
            positionByGroupId.delete(row.upsell_group_id);
          }
        });

      if (positionByGroupId.size === 0) return [];

      return onlineUpsellGroups
        .filter((group) => positionByGroupId.has(group.id))
        .map((group) => {
          const items = (upsellItemsByGroupId.get(group.id) ?? [])
            .map((row) => {
              const product = upsellProductsById.get(row.product_id);
              // Unknown, unavailable, or the product the customer is already
              // adding: an offer they cannot act on is worse than no offer.
              if (!product) return null;
              if (!product.isAvailableToday) return null;
              if (row.product_id === productId) return null;

              // An upsell is a one-tap yes. A product that still owes the
              // customer a mandatory choice cannot be offered that way without
              // putting an incomplete line on the order. Spelled out rather
              // than calling getRequiredSubgroupsOf: that helper is typed off
              // `Product`, which is inferred from this very function.
              const requiresChoice = buildProductOptionGroups(
                row.product_id,
                product.categoryId
              ).some(
                (subgroup) => (Number(subgroup.minOptions ?? 0) || 0) >= 1
              );
              if (requiresChoice) return null;

              const overridePrice =
                row.price_override_in_cents !== null
                  ? new Decimal(row.price_override_in_cents).div(100).toNumber()
                  : null;

              return {
                id: row.id,
                productId: row.product_id,
                name: row.name?.trim() || product.name,
                description: row.description,
                featuredImage: product.featuredImage,
                takeAwayPrice: overridePrice ?? product.takeAwayPrice,
                deliveryPrice: overridePrice ?? product.deliveryPrice,
                position: row.position,
              };
            })
            .filter((item): item is NonNullable<typeof item> => item !== null)
            .sort((a, b) => a.position - b.position);

          return {
            id: group.id,
            title: group.title?.trim() || group.name,
            description: group.description,
            maxSelections: group.max_selections ?? 0,
            position: positionByGroupId.get(group.id) ?? group.position ?? 0,
            items,
          };
        })
        .filter((group) => group.items.length > 0)
        .sort((a, b) => a.position - b.position);
    };

    // Filter products based on status and availability
    // - Active products: always include
    // - Inactive products: only include if there's a product_day_availability record for today with is_available = true
    const products = productsResult.data.filter((product) => {
      // If product is active, include it
      if (product.status === Status.active) {
        return true;
      }

      // If product is inactive, only include if there's an availability record for today with is_available = true
      if (product.status === Status.inactive) {
        return productAvailabilityMap.has(product.id) &&
          productAvailabilityMap.get(product.id) === true;
      }

      // For any other status, exclude it
      return false;
    });
    const categories = productCategoriesResult.data;

    // What is left on the shelf. Only the storefront sells from stock; the
    // restaurant site keeps marking products sold out per day instead.
    const availableStockByProductId = isStorefront
      ? await loadAvailableStock(supabase, userId, products)
      : new Map<string, number>();

    /** Storefront fields; harmless extras on the restaurant menu. */
    const buildStorefrontFields = (
      product: (typeof products)[number]
    ) => ({
      slug: product.slug ?? undefined,
      featured: product.featured ?? false,
      createdAt: product.createdDate ?? undefined,
      // The gallery in its own order, after the featured image, which the
      // gallery often repeats.
      images: Array.from(
        new Set(
          [
            product.featuredImage?.url,
            ...[...(product.images ?? [])]
              .sort((a, b) => a.position - b.position)
              .map((entry) => entry.image?.url),
          ].filter((url): url is string => !!url)
        )
      ),
      tagIds: tagIdsByProductId.get(product.id) ?? [],
      // null = unlimited; otherwise units that can still be sold.
      stock: availableStockByProductId.get(product.id) ?? null,
    });

    const categoryOptionGroups = categoryOptionGroupsResult.data;
    const tagOptionGroups = tagOptionGroupsResult.data;
    const productOptionGroups = productOptionGroupsResult.data;
    const allOptions = contextFilteredOptions;
    const categoryOptionOverrides = categoryOptionOverridesResult.data;
    // Resolve each product-level override with its contexts. A null contexts
    // value means "no explicit context rows" (applies in all contexts); a
    // non-null Set means an explicit list — used to gate online visibility.
    interface ResolvedProductOptionOverride {
      id: string;
      product_id: string;
      option_id: string;
      is_enabled: boolean;
      price_override_in_cents: number | null;
      contexts: Set<string> | null;
    }
    const productOptionOverrides: ResolvedProductOptionOverride[] =
      productOptionOverridesResult.data.map((override) => {
        const contexts = productOverrideContextsByOverrideId.get(override.id);
        return {
          ...override,
          contexts: contexts && contexts.size > 0 ? contexts : null,
        };
      });

    // Create maps for efficient lookup
    const optionsByGroupId = new Map<string, typeof allOptions>();
    allOptions.forEach((option) => {
      if (!optionsByGroupId.has(option.option_group_id)) {
        optionsByGroupId.set(option.option_group_id, []);
      }
      optionsByGroupId.get(option.option_group_id)!.push(option);
    });

    const categoryOptionGroupsByCategoryId = new Map<
      string,
      typeof categoryOptionGroups
    >();
    categoryOptionGroups.forEach((cog) => {
      if (!categoryOptionGroupsByCategoryId.has(cog.category_id)) {
        categoryOptionGroupsByCategoryId.set(cog.category_id, []);
      }
      categoryOptionGroupsByCategoryId.get(cog.category_id)!.push(cog);
    });

    // Shaped for orderTagOptionGroupLinks, which orders a product's tag groups
    const tagOptionGroupLinksByTagId = new Map<
      string,
      Array<{
        tagId: string;
        optionGroupId: string;
        position: number;
        tagPosition: number;
        row: (typeof tagOptionGroups)[number];
      }>
    >();
    tagOptionGroups.forEach((tog) => {
      if (!tagOptionGroupLinksByTagId.has(tog.tag_id)) {
        tagOptionGroupLinksByTagId.set(tog.tag_id, []);
      }
      tagOptionGroupLinksByTagId.get(tog.tag_id)!.push({
        tagId: tog.tag_id,
        optionGroupId: tog.option_group_id,
        position: tog.position,
        tagPosition: tog.tag?.position ?? 0,
        row: tog,
      });
    });

    const tagIdsByProductId = new Map<string, string[]>();
    productTagLinksResult.data.forEach((link) => {
      if (!tagIdsByProductId.has(link.product_id)) {
        tagIdsByProductId.set(link.product_id, []);
      }
      tagIdsByProductId.get(link.product_id)!.push(link.product_tag_id);
    });

    const productOptionGroupsByProductId = new Map<
      string,
      typeof productOptionGroups
    >();
    productOptionGroups.forEach((pog) => {
      if (!productOptionGroupsByProductId.has(pog.product_id)) {
        productOptionGroupsByProductId.set(pog.product_id, []);
      }
      productOptionGroupsByProductId.get(pog.product_id)!.push(pog);
    });

    const categoryOverridesByCategoryAndOption = new Map<
      string,
      Map<string, (typeof categoryOptionOverrides)[number]>
    >();
    categoryOptionOverrides.forEach((override) => {
      if (!categoryOverridesByCategoryAndOption.has(override.category_id)) {
        categoryOverridesByCategoryAndOption.set(
          override.category_id,
          new Map()
        );
      }
      categoryOverridesByCategoryAndOption
        .get(override.category_id)!
        .set(override.option_id, override);
    });

    const productOverridesByProductAndOption = new Map<
      string,
      Map<string, (typeof productOptionOverrides)[number]>
    >();
    productOptionOverrides.forEach((override) => {
      if (!productOverridesByProductAndOption.has(override.product_id)) {
        productOverridesByProductAndOption.set(
          override.product_id,
          new Map()
        );
      }
      productOverridesByProductAndOption
        .get(override.product_id)!
        .set(override.option_id, override);
    });

    // --- Resolve menu products' course structure ---
    // A menu product (type = "menu") offers a set of products per course. The
    // customer picks one item per course; each item may add a supplement on top
    // of the menu's fixed base price. Menu-scoped child products are excluded
    // from the main catalog query, so their names are fetched separately here.
    interface MenuCourseItemRow {
      menu_product_id: string;
      course_id: string;
      product_id: string;
      position: number;
      price_supplement_in_cents: number | null;
    }
    interface MenuCourseSettingRow {
      menu_product_id: string;
      course_id: string;
      is_required: boolean;
      position: number;
      name_override: string | null;
      pick_count: number | null;
    }
    interface CourseRow {
      id: string;
      name: string;
      position: number;
      status: string;
      menu_product_id: string | null;
    }

    const menuProductIdSet = new Set(
      products
        .filter((product) => product.type === ProductTypes.menu)
        .map((product) => product.id)
    );

    // If any of the menu queries failed, log and continue with empty data so a
    // menu issue never takes down the whole catalog.
    if (menuCourseItemsResult.error) {
      console.error(
        "Could not get menu course items",
        menuCourseItemsResult.error
      );
    }
    if (menuCourseSettingsResult.error) {
      console.error(
        "Could not get menu course settings",
        menuCourseSettingsResult.error
      );
    }
    if (menuCoursesResult.error) {
      console.error("Could not get menu courses", menuCoursesResult.error);
    }

    const manualMenuCourseItemRows = (
      (menuCourseItemsResult.data ?? []) as unknown as MenuCourseItemRow[]
    ).filter((row) => menuProductIdSet.has(row.menu_product_id));
    // Products a course offers through its tags. Like the other menu queries,
    // a failure only drops them rather than the whole catalog.
    const menuCourseTagItemsResult = await loadMenuCourseTagItemRows(
      supabase,
      userId,
      Array.from(menuProductIdSet),
      manualMenuCourseItemRows
    );
    if (menuCourseTagItemsResult.error) {
      console.error(
        "Could not get menu course tag items",
        menuCourseTagItemsResult.error
      );
    }
    const menuCourseItemRows: MenuCourseItemRow[] = [
      ...manualMenuCourseItemRows,
      // After the hand-picked items (the sort below is stable, so they keep
      // their name order), without a supplement.
      ...(menuCourseTagItemsResult.data ?? []).map((row) => ({
        ...row,
        position: Number.MAX_SAFE_INTEGER,
        price_supplement_in_cents: 0,
      })),
    ];
    const menuCourseSettingRows = (
      (menuCourseSettingsResult.data ?? []) as unknown as MenuCourseSettingRow[]
    ).filter((row) => menuProductIdSet.has(row.menu_product_id));
    const menuCourseRows = (menuCoursesResult.data ??
      []) as unknown as CourseRow[];

    // Fetch the names/status of every product offered inside a menu course.
    // These may be menu-scoped (excluded from the main query), so fetch by id.
    const menuChildProductIds = Array.from(
      new Set(menuCourseItemRows.map((row) => row.product_id))
    );
    const menuChildProductsById = new Map<
      string,
      { name: string; status: string; categoryId: string | null }
    >();
    if (menuChildProductIds.length > 0) {
      const menuChildProductsResult = await supabase
        .from("product")
        .select("id, name, display_name, status, product_category_id")
        .in("id", menuChildProductIds)
        .eq("user_id", userId)
        .neq("status", Status.trashed);

      if (menuChildProductsResult.error) {
        console.error(
          "Could not get menu child products",
          menuChildProductsResult.error
        );
      } else {
        menuChildProductsResult.data.forEach((child) => {
          menuChildProductsById.set(child.id, {
            name: child.display_name ?? child.name,
            status: child.status,
            categoryId: child.product_category_id ?? null,
          });
        });
      }
    }

    // buildMenuCourses is defined after buildProductOptionGroups (below) so it
    // can reuse it to resolve each menu item's own option groups.

    /**
     * Build variants for a product, resolving take-away and delivery prices
     * from the active online-order price tiers.
     */
    const buildProductVariants = (
      productVariants:
        | {
            id: string;
            name: string;
            position: number;
            status: string;
            prices: { priceId: string; value: number }[];
          }[]
        | null
        | undefined,
    ) => {
      const takeAwayPriceId =
        onlineOrderConfigurationResult.data.take_away_price_id;
      const deliveryPriceId =
        onlineOrderConfigurationResult.data.delivery_price_id;

      return [...(productVariants ?? [])]
        .filter((variant) => variant.status === Status.active)
        .sort((a, b) => a.position - b.position)
        .map((variant) => {
          const takeAway = variant.prices.find(
            (p) => p.priceId === takeAwayPriceId,
          );
          const delivery = variant.prices.find(
            (p) => p.priceId === deliveryPriceId,
          );
          return {
            id: variant.id,
            name: variant.name,
            position: variant.position,
            takeAwayPrice: takeAway?.value,
            deliveryPrice: delivery?.value,
          };
        });
    };

    /**
     * Build option groups for a product, applying hierarchy and overrides
     */
    const buildProductOptionGroups = (
      productId: string,
      categoryId: string | null
    ) => {
      const optionGroupsMap = new Map<
        string,
        {
          id: string;
          name: string;
          displayOrder: number;
          minOptions: number;
          maxOptions: number;
          productOptions: Array<{
            id: string;
            name: string;
            price: number;
            takeAwayPrice?: number;
            deliveryPrice?: number;
            isAvailableToday?: boolean;
          }>;
        }
      >();

      const productGroupOverrides =
        productGroupOverridesByProductAndGroup.get(productId);

      // 1. Add category-level option groups, then the groups linked to the
      // product's tags, numbered after the category's own groups. A group
      // reached both ways keeps its category position.
      const categoryGroups = categoryId
        ? (categoryOptionGroupsByCategoryId.get(categoryId) ?? [])
        : [];
      const tagIds = tagIdsByProductId.get(productId) ?? [];
      const tagGroups = orderTagOptionGroupLinks(
        tagIds.flatMap((tagId) => tagOptionGroupLinksByTagId.get(tagId) ?? []),
        {
          tagIds,
          skipGroupIds: new Set(categoryGroups.map((cog) => cog.option_group_id)),
          firstPosition:
            categoryGroups.length > 0
              ? Math.max(...categoryGroups.map((cog) => cog.position)) + 1
              : 0,
        }
      ).map(({ link, position }) => ({ ...link.row, position }));
      const inheritedGroups = [...categoryGroups, ...tagGroups];
      const productOverrides =
        productOverridesByProductAndOption.get(productId);

      inheritedGroups.forEach((cog) => {
        const optionGroup = cog.option_group;
        if (!optionGroup) return;
        if (optionGroupsMap.has(optionGroup.id)) return;

        // Apply product-level group override: a disabled override, or
        // contexts that are set but do not include "online", exclude this
        // group from this product's online options.
        const groupOverride = productGroupOverrides?.get(optionGroup.id);
        if (groupOverride && !groupOverride.isEnabled) {
          return;
        }
        if (
          groupOverride?.contexts &&
          !groupOverride.contexts.has(V2OptionGroupContexts.online)
        ) {
          return;
        }

        // In the group's own order: A-Z or the order dragged on the POS.
        const options = sortV2Options(
          optionsByGroupId.get(optionGroup.id) || [],
          optionGroup.option_sort_order,
        );
        const categoryOverrides = categoryId
          ? categoryOverridesByCategoryAndOption.get(categoryId)
          : undefined;

        const productOptions = options
          .filter((option) => {
            // Product override takes precedence over category override
            if (productOverrides?.has(option.id)) {
              const override = productOverrides.get(option.id)!;
              // If the override has explicit contexts and "online" is not
              // among them, this option is hidden online for this product.
              if (
                override.contexts &&
                !override.contexts.has(V2OptionGroupContexts.online)
              ) {
                return false;
              }
              return override.is_enabled;
            }
            // Then check category override
            if (categoryOverrides?.has(option.id)) {
              const override = categoryOverrides.get(option.id)!;
              return override.is_enabled;
            }
            return true;
          })
          .map((option) => {
            // Handle product type options differently
            if (option.type === V2OptionType.product && option.product_id) {
              const productData = productOptionsData.get(option.product_id);
              if (productData) {
                // When useProductPrice is on, the option always inherits the
                // linked product's current price; overrides and the option's
                // own price are ignored.
                if (option.use_product_price === true) {
                  return {
                    id: option.id,
                    name: productData.display_name ?? productData.name,
                    price: 0,
                    takeAwayPrice: productData.takeAwayPrice,
                    deliveryPrice: productData.deliveryPrice,
                    isAvailableToday: productData.isAvailableToday,
                  };
                }

                // Check for price overrides first
                let overridePrice: number | null = null;
                if (productOverrides?.has(option.id)) {
                  const override = productOverrides.get(option.id)!;
                  if (override.price_override_in_cents !== null) {
                    overridePrice = override.price_override_in_cents;
                  }
                } else if (categoryOverrides?.has(option.id)) {
                  const override = categoryOverrides.get(option.id)!;
                  if (override.price_override_in_cents !== null) {
                    overridePrice = override.price_override_in_cents;
                  }
                }

                // If there's a price override, use it for both prices
                if (overridePrice !== null) {
                  const overridePriceInEuros = new Decimal(overridePrice).div(100).toNumber();
                  return {
                    id: option.id,
                    name: productData.display_name ?? productData.name,
                    price: overridePriceInEuros,
                    takeAwayPrice: overridePriceInEuros,
                    deliveryPrice: overridePriceInEuros,
                    isAvailableToday: productData.isAvailableToday,
                  };
                }

                // If no override, check the option's price_in_cents
                // If price_in_cents is 0, use 0 (don't use product prices)
                if (option.price_in_cents === 0) {
                  return {
                    id: option.id,
                    name: productData.display_name ?? productData.name,
                    price: 0,
                    takeAwayPrice: 0,
                    deliveryPrice: 0,
                    isAvailableToday: productData.isAvailableToday,
                  };
                }

                // If price_in_cents is set and not 0, use it for both prices
                if (option.price_in_cents !== null && option.price_in_cents !== undefined) {
                  const priceInEuros = new Decimal(option.price_in_cents).div(100).toNumber();
                  return {
                    id: option.id,
                    name: productData.display_name ?? productData.name,
                    price: priceInEuros,
                    takeAwayPrice: priceInEuros,
                    deliveryPrice: priceInEuros,
                    isAvailableToday: productData.isAvailableToday,
                  };
                }

                // Only if price_in_cents is null/undefined, use the product's prices
                return {
                  id: option.id,
                  name: productData.display_name ?? productData.name,
                  price: 0, // Base price for product options is 0, use takeAwayPrice/deliveryPrice instead
                  takeAwayPrice: productData.takeAwayPrice,
                  deliveryPrice: productData.deliveryPrice,
                  isAvailableToday: productData.isAvailableToday,
                };
              }
              // If product data not found, skip this option
              return null;
            }

            // Handle regular single type options
            // Apply price overrides: product override > category override > base price
            let price = option.price_in_cents ?? 0;
            if (productOverrides?.has(option.id)) {
              const override = productOverrides.get(option.id)!;
              if (override.price_override_in_cents !== null) {
                price = override.price_override_in_cents;
              }
            } else if (categoryOverrides?.has(option.id)) {
              const override = categoryOverrides.get(option.id)!;
              if (override.price_override_in_cents !== null) {
                price = override.price_override_in_cents;
              }
            }

            return {
              id: option.id,
              name: option.name,
              price: new Decimal(price).div(100).toNumber(),
              isAvailableToday: true,
            };
          })
          .filter((option): option is NonNullable<typeof option> => option !== null);

        if (productOptions.length > 0) {
          optionGroupsMap.set(optionGroup.id, {
            id: optionGroup.id,
            name: optionGroup.name,
            displayOrder: cog.position ?? optionGroup.position ?? 0,
            minOptions:
              groupOverride?.minSelections ??
              optionGroup.min_selections ??
              0,
            maxOptions:
              groupOverride?.maxSelections ??
              optionGroup.max_selections ??
              0,
            productOptions,
          });
        }
      });

      // 2. Add/override with product-level option groups
      const productGroups = productOptionGroupsByProductId.get(productId);
      if (productGroups) {
        productGroups.forEach((pog) => {
          const optionGroup = pog.option_group;
          if (!optionGroup) return;

          // Apply product-level context override — skip if online not in set
          const groupOverride = productGroupOverrides?.get(optionGroup.id);
          if (
            groupOverride?.contexts &&
            !groupOverride.contexts.has(V2OptionGroupContexts.online)
          ) {
            return;
          }

          // In the group's own order: A-Z or the order dragged on the POS.
          const options = sortV2Options(
            optionsByGroupId.get(optionGroup.id) || [],
            optionGroup.option_sort_order,
          );
          const categoryOverrides = categoryId
            ? categoryOverridesByCategoryAndOption.get(categoryId)
            : undefined;
          const productOverrides =
            productOverridesByProductAndOption.get(productId);

          const productOptions = options
            .filter((option) => {
              // Product override takes precedence over category override
              if (productOverrides?.has(option.id)) {
                const override = productOverrides.get(option.id)!;
                // If the override has explicit contexts and "online" is not
                // among them, this option is hidden online for this product.
                if (
                  override.contexts &&
                  !override.contexts.has(V2OptionGroupContexts.online)
                ) {
                  return false;
                }
                return override.is_enabled;
              }
              // Then check category override
              if (categoryOverrides?.has(option.id)) {
                const override = categoryOverrides.get(option.id)!;
                return override.is_enabled;
              }
              return true;
            })
            .map((option) => {
              // Handle product type options differently
              if (option.type === V2OptionType.product && option.product_id) {
                const productData = productOptionsData.get(option.product_id);
                if (productData) {
                  // When useProductPrice is on, the option always inherits the
                  // linked product's current price; overrides and the option's
                  // own price are ignored.
                  if (option.use_product_price === true) {
                    return {
                      id: option.id,
                      name: productData.display_name ?? productData.name,
                      price: 0,
                      takeAwayPrice: productData.takeAwayPrice,
                      deliveryPrice: productData.deliveryPrice,
                      isAvailableToday: productData.isAvailableToday,
                    };
                  }

                  // Check for price overrides first
                  let overridePrice: number | null = null;
                  if (productOverrides?.has(option.id)) {
                    const override = productOverrides.get(option.id)!;
                    if (override.price_override_in_cents !== null) {
                      overridePrice = override.price_override_in_cents;
                    }
                  } else if (categoryOverrides?.has(option.id)) {
                    const override = categoryOverrides.get(option.id)!;
                    if (override.price_override_in_cents !== null) {
                      overridePrice = override.price_override_in_cents;
                    }
                  }

                  // If there's a price override, use it for both prices
                  if (overridePrice !== null) {
                    const overridePriceInEuros = new Decimal(overridePrice).div(100).toNumber();
                    return {
                      id: option.id,
                      name: productData.display_name ?? productData.name,
                      price: overridePriceInEuros,
                      takeAwayPrice: overridePriceInEuros,
                      deliveryPrice: overridePriceInEuros,
                      isAvailableToday: productData.isAvailableToday,
                    };
                  }

                  // If no override, check the option's price_in_cents
                  // If price_in_cents is 0, use 0 (don't use product prices)
                  if (option.price_in_cents === 0) {
                    return {
                      id: option.id,
                      name: productData.display_name ?? productData.name,
                      price: 0,
                      takeAwayPrice: 0,
                      deliveryPrice: 0,
                      isAvailableToday: productData.isAvailableToday,
                    };
                  }

                  // If price_in_cents is set and not 0, use it for both prices
                  if (option.price_in_cents !== null && option.price_in_cents !== undefined) {
                    const priceInEuros = new Decimal(option.price_in_cents).div(100).toNumber();
                    return {
                      id: option.id,
                      name: productData.display_name ?? productData.name,
                      price: priceInEuros,
                      takeAwayPrice: priceInEuros,
                      deliveryPrice: priceInEuros,
                      isAvailableToday: productData.isAvailableToday,
                    };
                  }

                  // Only if price_in_cents is null/undefined, use the product's prices
                  return {
                    id: option.id,
                    name: productData.display_name ?? productData.name,
                    price: 0, // Base price for product options is 0, use takeAwayPrice/deliveryPrice instead
                    takeAwayPrice: productData.takeAwayPrice,
                    deliveryPrice: productData.deliveryPrice,
                    isAvailableToday: productData.isAvailableToday,
                  };
                }
                // If product data not found, skip this option
                return null;
              }

              // Handle regular single type options
              // Apply price overrides: product override > category override > base price
              let price = option.price_in_cents ?? 0;
              if (productOverrides?.has(option.id)) {
                const override = productOverrides.get(option.id)!;
                if (override.price_override_in_cents !== null) {
                  price = override.price_override_in_cents;
                }
              } else if (categoryOverrides?.has(option.id)) {
                const override = categoryOverrides.get(option.id)!;
                if (override.price_override_in_cents !== null) {
                  price = override.price_override_in_cents;
                }
              }

              return {
                id: option.id,
                name: option.name,
                price: new Decimal(price).div(100).toNumber(),
                isAvailableToday: true,
              };
            })
            .filter((option): option is NonNullable<typeof option> => option !== null);

          if (productOptions.length > 0) {
            optionGroupsMap.set(optionGroup.id, {
              id: optionGroup.id,
              name: optionGroup.name,
              displayOrder: pog.position ?? optionGroup.position ?? 0,
              minOptions:
                pog.min_selections ??
                groupOverride?.minSelections ??
                optionGroup.min_selections ??
                0,
              maxOptions:
                pog.max_selections ??
                groupOverride?.maxSelections ??
                optionGroup.max_selections ??
                0,
              productOptions,
            });
          }
        });
      }

      return Array.from(optionGroupsMap.values()).sort(
        (a, b) => a.displayOrder - b.displayOrder
      );
    };

    /**
     * Build the ordered list of courses for a menu product. Each course keeps
     * only its currently-active offered items and courses with no items are
     * dropped. Mirrors the POS menu resolution (global courses plus courses
     * scoped to this menu, sorted by the menu's course order). Each item also carries its own
     * option groups (resolved via buildProductOptionGroups) so the customer can
     * satisfy any required product options on the chosen item.
     */
    const buildMenuCourses = (menuProductId: string) => {
      const settingByCourse = new Map(
        menuCourseSettingRows
          .filter((row) => row.menu_product_id === menuProductId)
          .map((row) => [row.course_id, row] as const)
      );

      const itemsByCourse = new Map<string, MenuCourseItemRow[]>();
      menuCourseItemRows
        .filter((row) => row.menu_product_id === menuProductId)
        .forEach((row) => {
          const existing = itemsByCourse.get(row.course_id);
          if (existing) existing.push(row);
          else itemsByCourse.set(row.course_id, [row]);
        });

      const activeCourses = menuCourseRows
        .filter(
          (course) =>
            course.status === Status.active &&
            (!course.menu_product_id ||
              course.menu_product_id === menuProductId)
        )
        .slice()
        // In the order the menu sets; a course it never placed keeps its own
        // position.
        .sort(
          (a, b) =>
            (settingByCourse.get(a.id)?.position ?? a.position) -
            (settingByCourse.get(b.id)?.position ?? b.position)
        );

      return activeCourses
        .map((course) => {
          const items = (itemsByCourse.get(course.id) ?? [])
            .slice()
            .sort((a, b) => a.position - b.position)
            .map((row) => {
              const child = menuChildProductsById.get(row.product_id);
              if (!child || child.status !== Status.active) return null;
              return {
                productId: row.product_id,
                name: child.name,
                priceSupplement: new Decimal(
                  row.price_supplement_in_cents ?? 0
                )
                  .div(100)
                  .toNumber(),
                productOptionSubgroups: buildProductOptionGroups(
                  row.product_id,
                  child.categoryId
                ),
              };
            })
            .filter((item): item is NonNullable<typeof item> => item !== null);

          const setting = settingByCourse.get(course.id);
          return {
            courseId: course.id,
            courseName: setting?.name_override || course.name,
            isRequired: setting?.is_required ?? true,
            // How many items are picked for this course per menu: exactly this
            // many when required, up to this many when optional.
            pickCount: normalizePickCount(setting?.pick_count),
            position: course.position,
            items,
          };
        })
        .filter((course) => course.items.length > 0);
    };

    // Group products by category
    const productsByCategory = categories.map((category) => {
      const categoryProducts = products
        .filter((product) => product.productCategoryId === category.id)
        .map((product) => {
          const takeAwayPrice = product.prices.find(
            (price) =>
              price.priceId ===
              onlineOrderConfigurationResult.data.take_away_price_id
          );
          const deliveryPrice = product.prices.find(
            (price) =>
              price.priceId ===
              onlineOrderConfigurationResult.data.delivery_price_id
          );
          // Check if product is available today (defaults to true if no record exists)
          const isAvailableToday = productsBlockedByIngredients.has(product.id)
            ? false
            : productAvailabilityMap.has(product.id)
              ? productAvailabilityMap.get(product.id) ?? true
              : true;

          const availabilityWindow = resolveAvailabilityWindow(
            product.available_from_date,
            product.available_until_date,
            category.available_from_date,
            category.available_until_date,
          );

          const timeWindow = resolveTimeWindow(
            product.available_from_time,
            product.available_until_time,
            category.available_from_time,
            category.available_until_time,
          );

          return {
            id: product.id,
            name: product.display_name ?? product.name,
            ageRestricted: product.age_restricted,
            visibility: product.visibility,
            categoryTarget: category.target,
            availableFromTime: timeWindow.fromTime,
            availableUntilTime: timeWindow.untilTime,
            availableFromDate: availabilityWindow.fromDate,
            availableUntilDate: availabilityWindow.untilDate,
            isWithinDateRange: availabilityWindow.isWithinDateRange,
            allergens: product.allergens.map((allergen) => allergen.allergen),
            position: product.onlineOrderPosition ?? product.position,
            featuredImage: product.featuredImage?.url ?? undefined,
            productCategoryId: category.id as string | null,
            isMenu: product.type === ProductTypes.menu,
            menuCourses:
              product.type === ProductTypes.menu
                ? buildMenuCourses(product.id)
                : [],
            productOptionSubgroups: buildProductOptionGroups(
              product.id,
              category.id
            ),
            upsells: buildProductUpsells(product.id, category.id),
            variants: buildProductVariants(
              (product as { variants?: Parameters<typeof buildProductVariants>[0] })
                .variants,
            ),
            takeAwayPrice: takeAwayPrice?.value
              ? new Decimal(takeAwayPrice.value).div(100).toNumber()
              : takeAwayPrice?.oldValue,
            deliveryPrice: deliveryPrice?.value
              ? new Decimal(deliveryPrice.value).div(100).toNumber()
              : deliveryPrice?.oldValue,
            isAvailableToday,
            ...(product.description && { description: product.description }),
            ...buildStorefrontFields(product),
          };
        });

      // Check if category is available today (defaults to true if no record exists)
      const isCategoryAvailableToday = categoryAvailabilityMap.has(category.id)
        ? categoryAvailabilityMap.get(category.id) ?? true
        : true;

      return {
        id: category.id,
        name: category.display_name ?? category.name,
        description: category.description,
        position: category.onlineOrderPosition ?? category.position,
        isAvailableToday: isCategoryAvailableToday,
        target: category.target,
        products: categoryProducts.filter(
          (product) =>
            product.deliveryPrice ||
            product.takeAwayPrice ||
            // Variable products carry their price on the variants, not on the
            // base product, so keep them when any variant is priced for online.
            product.variants.some(
              (variant) =>
                variant.takeAwayPrice != null || variant.deliveryPrice != null
            )
        ),
      };
    });

    // Also include products without a category
    const uncategorizedProducts = products
      .filter((product) => !product.productCategoryId)
      .map((product) => {
        const takeAwayPrice = product.prices.find(
          (price) =>
            price.priceId ===
            onlineOrderConfigurationResult.data.take_away_price_id
        );
        const deliveryPrice = product.prices.find(
          (price) =>
            price.priceId ===
            onlineOrderConfigurationResult.data.delivery_price_id
        );
        // Check if product is available today (defaults to true if no record exists)
        const isAvailableToday = productsBlockedByIngredients.has(product.id)
          ? false
          : productAvailabilityMap.has(product.id)
            ? productAvailabilityMap.get(product.id) ?? true
            : true;

        const availabilityWindow = resolveAvailabilityWindow(
          product.available_from_date,
          product.available_until_date,
          null,
          null,
        );

        const timeWindow = resolveTimeWindow(
          product.available_from_time,
          product.available_until_time,
          null,
          null,
        );

        return {
          id: product.id,
          name: product.display_name ?? product.name,
          ageRestricted: product.age_restricted,
          visibility: product.visibility,
          categoryTarget: null,
          availableFromTime: timeWindow.fromTime,
          availableUntilTime: timeWindow.untilTime,
          availableFromDate: availabilityWindow.fromDate,
          availableUntilDate: availabilityWindow.untilDate,
          isWithinDateRange: availabilityWindow.isWithinDateRange,
          allergens: product.allergens.map((allergen) => allergen.allergen),
          position: product.onlineOrderPosition ?? product.position,
          featuredImage: product.featuredImage?.url ?? undefined,
          isMenu: product.type === ProductTypes.menu,
          menuCourses:
            product.type === ProductTypes.menu
              ? buildMenuCourses(product.id)
              : [],
          productOptionSubgroups: buildProductOptionGroups(product.id, null),
          upsells: buildProductUpsells(product.id, null),
          productCategoryId: null,
          variants: buildProductVariants(
            (product as { variants?: Parameters<typeof buildProductVariants>[0] })
              .variants,
          ),
          takeAwayPrice: takeAwayPrice?.value
            ? new Decimal(takeAwayPrice.value).div(100).toNumber()
            : takeAwayPrice?.oldValue,
          deliveryPrice: deliveryPrice?.value
            ? new Decimal(deliveryPrice.value).div(100).toNumber()
            : deliveryPrice?.oldValue,
          isAvailableToday,
          ...(product.description && { description: product.description }),
          ...buildStorefrontFields(product),
        };
      });

    if (uncategorizedProducts.length > 0) {
      productsByCategory.push({
        id: "uncategorized",
        name: "Other",
        description: null,
        position: 999999, // Put uncategorized at the end
        isAvailableToday: true,
        target: null,
        products: uncategorizedProducts,
      });
    }

    return productsByCategory.filter(
      (category) => category.products.length > 0
    );
  });

export type Product = Awaited<
  ReturnType<typeof fetchProducts>
>[number]["products"][number];

type Menu = Awaited<ReturnType<typeof fetchProducts>>;

/**
 * A category target leaves a category in a channel when it is empty/null ("no
 * restriction") or names one of the channel's targets.
 */
const targetAllows = (
  target: CategoryTarget | undefined,
  allowed: Target[]
): boolean =>
  !target || target.length === 0 || allowed.some((t) => target.includes(t));

/**
 * Keeps the products (and non-empty categories) a channel can see. A product
 * needs one of the channel's visibility types — an empty list means hidden
 * everywhere — and a category target that does not shut the channel out.
 */
const filterMenu = (
  menu: Menu,
  visibilityTypes: ProductVisibilityTypes[],
  targets: Target[]
): Menu =>
  menu
    .filter((category) => targetAllows(category.target, targets))
    .map((category) => ({
      ...category,
      products: category.products.filter(
        (product) =>
          visibilityTypes.some((type) =>
            (product.visibility ?? []).includes(type)
          ) && targetAllows(product.categoryTarget, targets)
      ),
    }))
    .filter((category) => category.products.length > 0);

/**
 * The shop's own website: everything on take away or delivery. A product that
 * is only on QR ordering, or a category targeted at QR ordering alone, stays
 * off it.
 */
export const selectShopMenu = (menu: Menu): Menu =>
  filterMenu(
    menu,
    [ProductVisibilityTypes.pickup, ProductVisibilityTypes.delivery],
    [Target.pickup, Target.delivery]
  );

/**
 * The menu a guest sees at the table: products with the QR ordering
 * availability, in categories that do not exclude it.
 *
 * QR ordering runs on the ordinary pickup path (the take-away price list, the
 * pickup order type in the cart), and every availability check downstream asks
 * "can this be ordered for pickup?". So each product that made it onto the QR
 * menu is presented as orderable for pickup and nothing else — a QR-only dish
 * must not be greyed out for lacking the take-away toggle, and a take-away-only
 * one has already been dropped here instead of showing "only available for".
 */
export const selectQrMenu = (menu: Menu): Menu =>
  filterMenu(menu, [ProductVisibilityTypes.qr], [Target.qr]).map(
    (category) => ({
      ...category,
      target: null,
      products: category.products.map((product) => ({
        ...product,
        visibility: [ProductVisibilityTypes.pickup],
        categoryTarget: null,
      })),
    })
  );

/**
 * The storefront's catalogue: products with the webshop availability, in
 * categories that do not exclude it. Menu products (a restaurant's set menus)
 * are left off.
 *
 * Like QR ordering, the storefront runs on the ordinary order types — a parcel
 * is a delivery, collecting it at the shop a pickup — and prices both from its
 * single price list. So each catalogue product is presented as orderable for
 * both, and the cart's availability checks pass whichever method is chosen at
 * checkout.
 */
export const selectStorefrontCatalog = (menu: Menu): Menu =>
  filterMenu(menu, [ProductVisibilityTypes.webshop], [Target.webshop])
    .map((category) => ({
      ...category,
      target: null,
      products: category.products
        .filter((product) => !product.isMenu)
        .map((product) => ({
          ...product,
          visibility: [
            ProductVisibilityTypes.pickup,
            ProductVisibilityTypes.delivery,
          ],
          categoryTarget: null,
        })),
    }))
    .filter((category) => category.products.length > 0);

/** A single course of a menu product with its selectable items. */
export type MenuCourse = NonNullable<Product["menuCourses"]>[number];

/** A single selectable item within a menu course. */
export type MenuCourseItem = MenuCourse["items"][number];

/**
 * The courses a customer must make a selection for before the menu can be
 * added to the cart. Optional courses can be left unselected.
 */
export const getRequiredMenuCourses = (product: Product): MenuCourse[] =>
  (product.menuCourses ?? []).filter((course) => course.isRequired);

/**
 * The lowest possible total for a menu: its base price plus the cheapest
 * required-course supplements (once per pick the course asks for). Optional
 * courses never lower the price.
 */
export const getMenuStartingPrice = (
  product: Product,
  basePrice: number
): number =>
  getRequiredMenuCourses(product).reduce((total, course) => {
    if (course.items.length === 0) return total;
    const cheapestSupplement = Math.min(
      ...course.items.map((item) => item.priceSupplement)
    );
    return (
      total +
      (Number.isFinite(cheapestSupplement) ? cheapestSupplement : 0) *
        course.pickCount
    );
  }, basePrice);

export type ProductCategory = {
  id: string;
  name: string;
  position: number;
  products: Product[];
};

export type ProductsByCategory = ProductCategory[];

export type CategoryTarget =
  | Database["public"]["Enums"]["target"][]
  | null;

/**
 * A category with an empty/null target has no restriction. Otherwise the
 * category (and its products) can only be ordered with the order types in
 * the target list.
 */
export const isCategoryAvailableForOrderType = (
  target: CategoryTarget | undefined,
  orderType: OrderTypes
): boolean => {
  const categoryTarget = target ?? [];
  return (
    categoryTarget.length === 0 ||
    categoryTarget.includes(
      orderType === OrderTypes.delivery ? Target.delivery : Target.pickup
    )
  );
};

/**
 * A product is orderable with an order type when its own visibility includes
 * that order type and its category's target does not exclude it. An empty/null
 * visibility list means the product is hidden from every order type, while an
 * empty/null category target still means "no restriction".
 */
export const isProductAvailableForOrderType = (
  product: Pick<Product, "visibility" | "categoryTarget">,
  orderType: OrderTypes
): boolean => {
  const visibility = product.visibility ?? [];
  const visibilityAllows = visibility.includes(
    orderType === OrderTypes.delivery
      ? ProductVisibilityTypes.delivery
      : ProductVisibilityTypes.pickup
  );
  return (
    visibilityAllows &&
    isCategoryAvailableForOrderType(product.categoryTarget, orderType)
  );
};

export const getAvailableOrderTypes = (
  product: Pick<Product, "visibility" | "categoryTarget">
): OrderTypes[] =>
  [OrderTypes.delivery, OrderTypes.pickup].filter((orderType) =>
    isProductAvailableForOrderType(product, orderType)
  );

/**
 * Required option subgroup requirement.
 * A subgroup is considered required if it has minOptions >= 1.
 */
export type RequiredSubgroup = { id: string; minOptions: number };

/**
 * The required option subgroups within any resolved subgroup list (a product's
 * or a menu item's). A subgroup is required when it has minOptions >= 1.
 */
export const getRequiredSubgroupsOf = (
  subgroups: Product["productOptionSubgroups"]
): RequiredSubgroup[] => {
  const required: RequiredSubgroup[] = [];

  subgroups.forEach((subgroup) => {
    const minOptions = Number(subgroup.minOptions ?? 0) || 0;
    if (minOptions >= 1) {
      required.push({ id: subgroup.id, minOptions });
    }
  });

  return required;
};

export const getRequiredSubgroups = (product: Product): RequiredSubgroup[] =>
  getRequiredSubgroupsOf(product.productOptionSubgroups);

export const getRequiredSubgroupIds = (product: Product): string[] =>
  getRequiredSubgroups(product).map((subgroup) => subgroup.id);

/** A single online-ordering variant with its resolved per-order-type prices. */
export type ProductVariant = NonNullable<Product["variants"]>[number];

/**
 * The variant's price for the given order type's price tier, if one is
 * configured. `undefined` means the variant is not sold for that order type.
 */
export const getVariantPriceForOrderType = (
  variant: ProductVariant,
  orderType: OrderTypes
): number | undefined =>
  orderType === OrderTypes.pickup
    ? variant.takeAwayPrice
    : variant.deliveryPrice;

/**
 * The variants of a product that are usable for online ordering with the given
 * order type. A variant is usable when it has a price configured for that order
 * type's tier. A product with at least one such variant is "variable"; when it
 * has exactly one, callers can apply it automatically instead of prompting.
 */
export const getAvailableVariantsForOrderType = (
  product: Pick<Product, "variants">,
  orderType: OrderTypes
): ProductVariant[] =>
  (product.variants ?? []).filter(
    (variant) => getVariantPriceForOrderType(variant, orderType) !== undefined
  );

/** One upsell offer ("Make it a meal?") with the products it offers. */
export type ProductUpsellGroup = NonNullable<Product["upsells"]>[number];

/** A single product offered inside an upsell group. */
export type ProductUpsellItem = ProductUpsellGroup["items"][number];

/**
 * The upsell item's price for the order type's tier. `undefined` means the
 * product is not sold for that order type, so the offer cannot be made.
 */
export const getUpsellPriceForOrderType = (
  item: ProductUpsellItem,
  orderType: OrderTypes
): number | undefined =>
  orderType === OrderTypes.pickup ? item.takeAwayPrice : item.deliveryPrice;

/**
 * The upsell groups worth showing for the current order type: groups keep only
 * the items priced for that tier, and a group left with nothing is dropped.
 */
export const getAvailableUpsellGroups = (
  product: Pick<Product, "upsells">,
  orderType: OrderTypes
): ProductUpsellGroup[] =>
  (product.upsells ?? [])
    .map((group) => ({
      ...group,
      items: group.items.filter(
        (item) => getUpsellPriceForOrderType(item, orderType) !== undefined
      ),
    }))
    .filter((group) => group.items.length > 0);
